import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import {
  Button as MUIButton,
  IconButton,
  Paper,
  Stack,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { MoreOptions } from '../Table/assets/MoreOptions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { contact } from '../../schema/contact';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import { serviceProviderContact } from '../../schema/serviceProviderContact';
import api from '../../services/api';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import toast from 'react-hot-toast';
import { AddNewContact } from '../Activity/AddNewContactForm';

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const ActivityContact = ({
  schema,
  values,
  fullWidth,
}: {
  schema: any;
  values: any;
  fullWidth: boolean;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>(undefined);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [query, setQuery] = React.useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = React.useState<
    any
  >({
    schema: schema,
    row: {},
    key: undefined,
  });

  const fetchContacts = useCallback(() => {
    const searchParams = new URLSearchParams({
      activity: values?.id,
    });

    api.get('/activity-contacts', searchParams).then((res: any) => {
      setProperties(res);
    });
  }, []);

  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <>
      <TableContainer component={Paper}>
        <Table
          aria-label="simple table"
          size="small"
          sx={{
            '& .MuiTableBody-root .MuiTableRow-root': {
              background: '#fff',
            },
          }}
        >
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                color: 'inherit',
                border: 'none',
              },
              '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                border: 'none',
                fontSize: '14px',
                p: '10px',
              },
              background: '#256861',
              color: '#fff',
            }}
          >
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  overflowWrap: 'anywhere',
                },
              }}
            >
              <TableCell></TableCell>
              <TableCell>Person</TableCell>
              {fullWidth && <TableCell>Phones</TableCell>}
              <TableCell>Email</TableCell>

              <TableCell>Service Provider</TableCell>
              <TableCell>Website</TableCell>

              <TableCell>Role</TableCell>
            </TableRow>
          </TableHead>
          {properties &&
            properties.map((contactUser: any) => {
              return (
                <TableRow>
                  <TableCell
                    sx={{
                      padding: '5px',
                    }}
                  >
                    <MoreOptions
                      clickOnEdit={() => {
                        setOpenedSlidingPane({
                          schema: serviceProviderContact,
                          row: contactUser,
                          tab: 'services',
                        });
                      }}
                      clickOnDelete={() => {
                        setOpenConfirmDialog({
                          row: contactUser,
                          service_provider: contactUser?.service_provider,
                          tab: 'contacts',
                          schema: contact,
                        });
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '5px',
                    }}
                  >
                    <Stack
                      sx={{
                        '& > a': {
                          textDecoration: 'underline',
                        },
                        cursor: 'pointer',
                        '&:hover': {
                          background: '#f3f3f3',
                        },
                      }}
                      onClick={() => {
                        setOpenedSlidingPane({
                          schema: contact,
                          row: contactUser,
                          tab: 'services',
                        });
                      }}
                    >
                      {fullWidth ? (
                        <>
                          <b>{contactUser?.name}</b>
                        </>
                      ) : (
                        <>
                          <b>{contactUser?.name}</b>
                          {contactUser?.phone && (
                            <a href={`tel:${contactUser?.phone}`}>
                              {contactUser?.phone}
                            </a>
                          )}
                          {contactUser?.alternative_phone && (
                            <a href={`tel:${contactUser?.alternative_phone}`}>
                              {contactUser?.alternative_phone}
                            </a>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>

                  {fullWidth && (
                    <TableCell
                      sx={{
                        padding: '5px',
                      }}
                    >
                      <Stack
                        sx={{
                          '& > a': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {contactUser?.phone && (
                          <a href={`tel:${contactUser?.phone}`}>
                            {contactUser?.phone}
                          </a>
                        )}
                        {contactUser?.alternative_phone && (
                          <a href={`tel:${contactUser?.alternative_phone}`}>
                            {contactUser?.alternative_phone}
                          </a>
                        )}
                      </Stack>
                    </TableCell>
                  )}

                  <TableCell
                    sx={{
                      padding: '5px',
                    }}
                  >
                    <Stack
                      sx={{
                        '& > a': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {contactUser?.email && (
                        <a href={`mailto:${contactUser.email}`}>
                          {contactUser.email}
                        </a>
                      )}
                      {contactUser?.alternative_email && (
                        <a href={`mailto:${contactUser.alternative_email}`}>
                          {contactUser.alternative_email}
                        </a>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '5px',
                    }}
                  >
                    {contactUser?.service_provider?.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '5px',
                    }}
                  >
                    <div dangerouslySetInnerHTML={{__html: contactUser?.service_provider?.website}}>

                    </div>
                  </TableCell>
                  <TableCell
                    sx={{
                      padding: '5px',
                    }}
                  >
                    {contactUser?.roles && (
                      <>
                        <Stack>
                          {contactUser.roles.map((role: any) => {
                            return <span>{role?.name}</span>;
                          })}
                        </Stack>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
        </Table>
      </TableContainer>

      <Stack
        direction={'row'}
        alignItems={'center'}
        sx={{
          width: '100%',
        }}
        justifyContent={'space-between'}
      >
        <Stack direction={'row'}>
          <Tooltip
            title={`Add new Contact`}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -14],
                    },
                  },
                ],
              },
            }}
          >
            <IconButton
              color={'primary'}
              onClick={() => {
                setCurrentAddNewPaneEntity({
                  row: {
                    id: 'new',
                  },
                  schema: serviceProviderContact,
                  key: 'service_provider',
                });
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack>
          {query === 'success' ? (
            <Stack direction={'row'} alignItems={'center'}>
              <CloudDoneRoundedIcon color={'disabled'} />
              <Typography variant={'caption'} marginLeft={'5px'} color={'gray'}>
                successfully saved
              </Typography>
            </Stack>
          ) : (
            <Fade
              in={query === 'progress'}
              style={{
                transitionDelay: query === 'progress' ? '800ms' : '0ms',
              }}
              unmountOnExit
            >
              <CircularProgress size={20} />
            </Fade>
          )}
        </Stack>
      </Stack>

      {currentAddNewPaneEntity?.row?.id && (
        <>
          {/* <SlidingPaneAddNew
            schema={currentAddNewPaneEntity?.schema}
            mainSchema={schema}
            id={currentAddNewPaneEntity?.row?.id}
            hasSlidingPane={false}
            onSubmit={(result: any) => {
              fetchContacts();
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });

              toast.success(`Successfully added ${schema.name}`);

              setQuery('progress');
              timerRef.current = setTimeout(() => {
                setQuery('success');
              }, 2000);
            }}
            propertiesFromTable={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            row={
              currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
            }
            onClose={(value) => {
              setCurrentAddNewPaneEntity({
                schema: schema,
                row: {},
                key: undefined,
              });
            }}
          />*/}
          <AddNewContact
            schema={serviceProviderContact}
            onClose={() => setCurrentAddNewPaneEntity(undefined)}
            onSubmit={() => {
              fetchContacts();
              setCurrentAddNewPaneEntity(undefined);
            }}
            activity={values}
          />
        </>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;
                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, { id: deletedRow.id })
                  .then((res: any) => {
                    const tempProperties = [...properties];

                    const newRows = tempProperties.filter((contact: any) => {
                      return contact?.id !== deletedRow?.id;
                    });
                    setProperties(newRows);
                    toast.success(
                      `Successfully deleted ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                    setOpenConfirmDialog(false);

                    setQuery('progress');
                    timerRef.current = setTimeout(() => {
                      setQuery('success');
                    }, 2000);
                  })
                  .catch((e: any) => {
                    console.log(e);
                    toast.error(
                      `Error: Unable to delete ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                  });
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            setOpenedSlidingPane({});
            fetchContacts();

            toast.success(`Successfully updated ${schema.name}`);

            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 2000);
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
